import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Brain, Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles } from 'lucide-react';
import { useToast } from '../../components/ui/toast';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [oauthStatus, setOauthStatus] = useState({ google: false, facebook: false });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  // FAZ 7: Login sonrası her zaman Ritual Entry'ye yönlendir
  // Entry ekranı, kullanıcıyı zihinsel olarak hazırladıktan sonra panel'e yönlendirir
  const from = '/panel/entry';

  // Check OAuth status on mount
  useEffect(() => {
    const checkOAuthStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/oauth/status`);
        if (res.ok) {
          const data = await res.json();
          setOauthStatus({
            google: data.google?.configured || false,
            facebook: data.facebook?.configured || false
          });
        }
      } catch (e) {
        console.log('OAuth status check failed');
      }
    };
    checkOAuthStatus();
  }, [API_URL]);

  // Check for OAuth errors in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthError = params.get('oauth_error');
    const message = params.get('message');
    
    if (oauthError) {
      addToast(message || 'Sosyal giriş başarısız oldu', 'error');
      // Clear URL params
      navigate('/giris', { replace: true });
    }
  }, [location.search, addToast, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(formData.email, formData.password);
      addToast('Giriş başarılı!', 'success');
      // Admin kullanıcıyı doğrudan admin paneline yönlendir
      const redirectTo = data.user?.role === 'admin' ? '/admin' : '/panel/entry';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      addToast(error.message || 'Giriş başarısız', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    try {
      const res = await fetch(`${API_URL}/api/auth/oauth/google/start`);
      const data = await res.json();
      if (data.success && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        addToast(data.error || 'Google bağlantı hatası', 'error');
        setSocialLoading(null);
      }
    } catch (error) {
      addToast('Google bağlantı hatası', 'error');
      setSocialLoading(null);
    }
  };

  const handleFacebookLogin = async () => {
    setSocialLoading('facebook');
    try {
      const res = await fetch(`${API_URL}/api/auth/oauth/facebook/start`);
      const data = await res.json();
      if (data.success && data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        addToast(data.error || 'Facebook bağlantı hatası', 'error');
        setSocialLoading(null);
      }
    } catch (error) {
      addToast('Facebook bağlantı hatası', 'error');
      setSocialLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950/30 to-gray-950 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Bilinçaltı Dönüşüm</span>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white">Hoş Geldiniz</h1>
            <p className="mt-2 text-white/50">Hesabınıza giriş yapın</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-white/60 text-sm mb-2">E-posta</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@email.com"
                  required
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500" />
                <span className="text-white/50 text-sm">Beni hatırla</span>
              </label>
              <Link to="/sifremi-unuttum" className="text-purple-400 text-sm hover:text-purple-300">
                Şifremi unuttum
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Giriş Yap
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          {/* Social Login Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-black/40 text-white/40">veya</span>
            </div>
          </div>

          {/* Social Login Buttons */}
          <div className="space-y-3">
            {/* Google Login */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={socialLoading || !oauthStatus.google}
              className="w-full py-3.5 bg-white hover:bg-gray-100 rounded-xl text-gray-800 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {socialLoading === 'google' ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-gray-800 rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google ile Giriş Yap
                </>
              )}
            </button>

            {/* Facebook Login - only show if configured */}
            {oauthStatus.facebook && (
            <button
              type="button"
              onClick={handleFacebookLogin}
              disabled={socialLoading}
              className="w-full py-3.5 rounded-xl font-medium transition-all flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white"
            >
              {socialLoading === 'facebook' ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook ile Giris Yap
                </>
              )}
            </button>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-white/50">
              Hesabınız yok mu?{' '}
              <Link to="/kayit" className="text-purple-400 hover:text-purple-300 font-medium">
                Ücretsiz Kaydol
              </Link>
            </p>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="mt-8 text-center text-white/30 text-sm">
          Giriş yaparak{' '}
          <Link to="/gizlilik" className="text-white/50 hover:text-white/70">Gizlilik Politikası</Link>
          {' '}ve{' '}
          <Link to="/kullanim-kosullari" className="text-white/50 hover:text-white/70">Kullanım Koşulları</Link>
          'nı kabul etmiş olursunuz.
        </p>
      </motion.div>
    </div>
  );
}
